import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CircleDot, Copy, Check, ExternalLink, Smartphone, Info, Send } from "lucide-react";
import { WhatsAppIcon } from "@/components/ui/whatsapp-icon";

interface WhatsAppShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shareData: {
    title: string;
    price: number;
    imageUrl?: string;
    shareableLink: string; // Should be /og-share?productId=xxx&checkout=true
  };
}

export function WhatsAppShareDialog({ open, onOpenChange, shareData }: WhatsAppShareDialogProps) {
  const [copied, setCopied] = useState(false);
  const [mode, setMode] = useState<"select" | "status">("select");

  // Share just the link - WhatsApp fetches OG tags for rich preview
  const shareUrl = shareData.shareableLink;

  const openWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(shareUrl)}`;
    window.open(url, "_blank");
    toast.success("Choose a contact on WhatsApp");
    onOpenChange(false);
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Link copied! Paste it on WhatsApp Status");
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <WhatsAppIcon className="h-5 w-5 text-[hsl(var(--whatsapp))]" />
            Share on WhatsApp
          </DialogTitle>
          <DialogDescription>
            {mode === "select"
              ? "Choose how you want to share this product"
              : "How to post this product to your Status"}
          </DialogDescription>
        </DialogHeader>

        {mode === "select" && (
          <div className="space-y-3 pt-2">
            <Card onClick={openWhatsApp} className="cursor-pointer hover:shadow transition-shadow">
              <CardContent className="p-4 flex items-start gap-4">
                <Send className="text-primary mt-1" />
                <div className="flex-1">
                  <p className="font-semibold">Send to Contact</p>
                  <p className="text-sm text-muted-foreground">Share directly with a customer or friend</p>
                </div>
                <ExternalLink className="text-muted-foreground mt-1" />
              </CardContent>
            </Card>

            <Card onClick={() => setMode("status")} className="cursor-pointer hover:shadow transition-shadow">
              <CardContent className="p-4 flex items-start gap-4">
                <CircleDot className="text-[hsl(var(--whatsapp))] mt-1" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold">Post to Status</p>
                    <Badge variant="secondary">Popular</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">Let all your contacts see this product</p>
                </div>
                <ExternalLink className="text-muted-foreground mt-1" />
              </CardContent>
            </Card>

            <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
              {shareData.imageUrl && (
                <img src={shareData.imageUrl} alt={shareData.title} className="w-12 h-12 rounded object-cover" />
              )}
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">{shareData.title}</p>
                <p className="text-sm text-muted-foreground">₦{shareData.price.toLocaleString()}</p>
              </div>
            </div>
          </div>
        )}

        {mode === "status" && (
          <div className="space-y-4 pt-2">
            <div className="bg-[hsl(var(--whatsapp))]/10 p-3 rounded-lg text-sm flex gap-2">
              <Info className="shrink-0 mt-0.5" />
              <span>Copy the link below and paste it into your WhatsApp Status. The product preview will appear automatically!</span>
            </div>

            <Button onClick={copyLink} className="w-full">
              {copied ? <Check className="mr-2" /> : <Copy className="mr-2" />}
              {copied ? "Copied!" : "Copy Share Link"}
            </Button>

            <Button onClick={openWhatsApp} variant="whatsapp" className="w-full">
              <Smartphone className="mr-2" />
              Open WhatsApp
            </Button>

            <Button variant="ghost" className="w-full" onClick={() => setMode("select")}>
              ← Back
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
